import { createApiClient } from "@ametie/vue-muza-use";
import type { InternalAxiosRequestConfig } from "axios";
import { isAxiosError } from "axios";

import { ensureToolsToken, TOOLS_UNAVAILABLE_MESSAGE, toolsSession } from "./toolsAuth";

export const TOOLS_API_BASE_URL = import.meta.env.VITE_TOOLS_API_URL
  ? `${import.meta.env.VITE_TOOLS_API_URL}`
  : "/tools-api";

// Tools REST carries the Tools token, never the insights one: insights mints it
// and `toolsSession` owns it (design add-tools-token-auth D1, D2). A 401 renews
// it once through the Tools session and retries; it never logs out of insights.
export const toolsAxios = createApiClient({
  baseURL: TOOLS_API_BASE_URL,
  timeout: 2 * 60 * 1000, // 2 minutes timeout
  authSession: toolsSession,
});

// `authMode` is a library extension of the request config, not part of axios' type.
const isPublicRequest = (config: InternalAxiosRequestConfig): boolean =>
  "authMode" in config && config.authMode === "public";

// Axios runs request interceptors last-registered-first, so this one resolves
// before the session's interceptor reads the stored token: a cold start or a
// token about to expire is renewed first, with no failed attempt. A failed mint
// rejects the request with its ToolsTokenError.
toolsAxios.interceptors.request.use(async (config) => {
  if (!isPublicRequest(config)) await ensureToolsToken();
  return config;
});

// The 401 path never reacts to a 503, so it never mints. `useApi` builds the
// message from `response.data.message` first, so a 503 body is given the
// unavailable message; the AxiosError itself (status, config) stays intact.
toolsAxios.interceptors.response.use(
  response => response,
  (error: unknown) => {
    if (isAxiosError(error) && error.response?.status === 503) {
      const body: unknown = error.response.data;
      error.message = TOOLS_UNAVAILABLE_MESSAGE;
      error.response.data = {
        ...(typeof body === "object" && body !== null ? body : {}),
        message: TOOLS_UNAVAILABLE_MESSAGE,
      };
    }
    return Promise.reject(error);
  },
);
